import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ScrollArea } from "@mantine/core";
import { BiUser } from "react-icons/bi";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { HiOutlinePresentationChartBar } from "react-icons/hi";
import styles from "./Customdropdown.module.css";


// Type definitions
interface DropdownItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
}

interface CustomDropdownProps {
  items: readonly DropdownItem[];
  buttonLabel: any;
}

// Constants moved outside component to prevent recreation
const ROUTE_LABELS = {
  "/location": "Location",
  "/manage-products": "Products",
  "/manage-verticals": "Verticals",
  "/manage-products-category": "Product Category",
  "/manage-raw-material": "Raw Material",
  "/material-subtype": "Material Sub Type",
  "/transport": "Transport",
  "/packaging": "Packaging",
  "/customers": "Customers",
  "/pallets": "Pallets",
  "/package-box": "Package-Box",
} as const;

const COMPUTE_LCA_ROUTES = {
  "/newlca": "New LCA",
  "/draft": "Draft",
} as const;


// Memoized DropdownItem component
const DropdownItem = memo(({
  item,
  isActive,
  onClick,
  refProp
}: {
  item: DropdownItem;
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
  refProp?: React.RefObject<HTMLDivElement>;
}) => (
  <div
    ref={refProp}
    className={`${styles.dropdownItem} ${isActive ? styles.activeDropdownItem : ""}`}
    onClick={onClick}
  >
    {item.icon}{item.label}
  </div>
));

DropdownItem.displayName = 'DropdownItem';

const CustomDropdown: React.FC<CustomDropdownProps> = ({ items, buttonLabel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null); // Ref for selected item

  const isRouteMatched = useMemo(() => {
    return items.some((item) => location.pathname.startsWith(item.href));
  }, [items, location.pathname]);
  
  useEffect(() => {
    setIsOpen(isRouteMatched);
  }, [isRouteMatched]);
  

  useEffect(() => {
    if (isOpen && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        // behavior: "smooth",
        block: "nearest"
      });
    }
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleItemClick = useCallback((e: React.MouseEvent, href: string) => {
    e.stopPropagation();
    navigate(href);
  }, [navigate]);

  const icon = buttonLabel === "Compute LCA"
    ? <HiOutlinePresentationChartBar size={20} />
    : <BiUser size={20} />;

  return (
    <div className={styles.customdropdown}>
      <div className={styles.dropdown} ref={dropdownRef}>
        {isOpen && <div className={styles.dropdownBorder} />}

        <button
          className={isOpen ? styles.btn : styles.unbtn}
          onClick={toggleDropdown}
        >
          <span className={isOpen ? styles.dropdownButton : styles.dropdownButtondisable}>
            {icon}
            {buttonLabel}
            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </button>

        {isOpen && (
          <div className={styles.maindropdownitem}>
            <div className={styles.dropdownMenu}>
              <ScrollArea
                h={buttonLabel === "Compute LCA" ? 106 : 300}
                scrollbarSize={3}
                scrollHideDelay={500}
              >
                {items.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <DropdownItem
                      key={item.href}
                      item={item}
                      isActive={isActive}
                      onClick={(e) => handleItemClick(e, item.href)}
                      refProp={isActive ? activeItemRef : undefined} // Assign ref to active item
                    />
                  );
                })}
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CustomDropdown);
